import type { FC, FormEvent, ReactNode } from 'react'
import type { FeedbackResponse } from '@/types'

import { useId, useState } from 'react'
import { CircleCheck, LoaderCircle, Send } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'

import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger
} from '@/components/ui/dialog'

type Status = 'idle' | 'sending' | 'sent' | 'error'

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

interface FeedbackDialogProps {
    children: ReactNode
    className?: string
}

export const FeedbackDialog: FC<FeedbackDialogProps> = ({ children, className }): ReactNode => {
    const id = useId()
    const [open, setOpen] = useState(false)
    const [message, setMessage] = useState('')
    const [email, setEmail] = useState('')
    const [website, setWebsite] = useState('')
    const [status, setStatus] = useState<Status>('idle')
    const [error, setError] = useState('')

    const reset = (): void => {
        setMessage('')
        setEmail('')
        setWebsite('')
        setStatus('idle')
        setError('')
    }

    const handleOpenChange = (next: boolean): void => {
        setOpen(next)

        if (!next && status === 'sent') reset()
    }

    const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        const trimmed = message.trim()

        if (trimmed.length < 5) {
            setError('Please write a little more so we know what you mean.')
            return
        }

        if (email.trim() && !EMAIL_PATTERN.test(email.trim())) {
            setError('That email address does not look right. You can also leave it empty.')
            return
        }

        setError('')
        setStatus('sending')

        try {
            const response = await fetch('/api/feedback', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ message: trimmed, email: email.trim(), website })
            })

            const data = await response.json() as FeedbackResponse

            if (!data.ok) {
                setStatus('error')
                setError(data.message)
                return
            }

            setStatus('sent')
        }
        catch {
            setStatus('error')
            setError('We could not send that right now. Please check your connection and try again.')
        }
    }

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogTrigger className={className}>
                {children}
            </DialogTrigger>

            <DialogContent>
                {status === 'sent' ? (
                    <div className='flex flex-col items-center gap-4 py-4 text-center'>
                        <span className='grid size-14 place-items-center rounded-full bg-accent text-primary'>
                            <CircleCheck className='size-7' aria-hidden='true' />
                        </span>

                        <DialogTitle>
                            Thanks, message received
                        </DialogTitle>

                        <DialogDescription className='max-w-xs'>
                            We read every message. If you left your email, we may get back to you there.
                        </DialogDescription>

                        <DialogClose render={<Button size='lg' className='mt-2 w-full sm:w-auto' />}>
                            Close
                        </DialogClose>
                    </div>
                ) : (
                    <>
                        <DialogHeader>
                            <DialogTitle>
                                Send us feedback
                            </DialogTitle>

                            <DialogDescription>
                                Found a link that will not download, or have an idea? Tell us, it helps CaveDL get better.
                            </DialogDescription>
                        </DialogHeader>

                        <form className='flex flex-col gap-4' onSubmit={handleSubmit} noValidate>
                            <div className='flex flex-col gap-2'>
                                <label htmlFor={`${id}-message`} className='text-sm font-semibold'>
                                    Your message
                                </label>

                                <Textarea
                                    id={`${id}-message`}
                                    value={message}
                                    maxLength={2000}
                                    required
                                    placeholder='What happened, or what would you like to see?'
                                    onChange={(event) => setMessage(event.target.value)}
                                />
                            </div>

                            <div className='flex flex-col gap-2'>
                                <label htmlFor={`${id}-email`} className='text-sm font-semibold'>
                                    Email
                                    <span className='font-normal text-muted-foreground'>
                                        {' '}(optional, only if you want a reply)
                                    </span>
                                </label>

                                <Input
                                    id={`${id}-email`}
                                    type='email'
                                    inputMode='email'
                                    autoComplete='email'
                                    value={email}
                                    maxLength={254}
                                    placeholder='you@example.com'
                                    onChange={(event) => setEmail(event.target.value)}
                                />
                            </div>

                            <div aria-hidden='true' className='sr-only'>
                                <label htmlFor={`${id}-website`}>
                                    Leave this empty
                                </label>

                                <input
                                    id={`${id}-website`}
                                    tabIndex={-1}
                                    autoComplete='off'
                                    value={website}
                                    onChange={(event) => setWebsite(event.target.value)}
                                />
                            </div>

                            <p role='alert' aria-live='polite' className='min-h-5 text-sm text-destructive'>
                                {error}
                            </p>

                            <Button type='submit' size='lg' disabled={status === 'sending'} className='w-full'>
                                {status === 'sending' ? (
                                    <LoaderCircle className='animate-spin' aria-hidden='true' />
                                ) : (
                                    <Send aria-hidden='true' />
                                )}

                                {status === 'sending' ? 'Sending...' : 'Send feedback'}
                            </Button>
                        </form>
                    </>
                )}
            </DialogContent>
        </Dialog>
    )
}
